import type { useNavigationContainerRef } from 'expo-router';

import { resetMountedTabs } from '@/components/layout/tabMountRegistry';

type NavigationContainerRef = ReturnType<typeof useNavigationContainerRef>;

const findAppRouteKey = (rootState: ReturnType<NavigationContainerRef['getRootState']>) => {
  const rootRoute = rootState?.routes?.[rootState?.index ?? 0];
  const nested = (rootRoute as { state?: { routes?: { name: string; key?: string }[] } })?.state;
  return nested?.routes?.find((route) => route.name === '(app)')?.key;
};

/**
 * Reset the whole navigation tree to a fresh (app) route. Used whenever the
 * community context changes under a live app tree: every tab would otherwise
 * keep its opened child screens and refetch them against the new community's
 * API. Position-independent — works no matter which screen triggered the
 * change (header switcher, directory, push notification, deep link), which
 * per-navigator router.* calls cannot do (they only reach the focused stack).
 *
 * This is the one place the app touches expo-router's PRIVATE state shape,
 * and both constraints were established empirically (task 166) — re-verify
 * this function on every expo-router upgrade:
 * - The reset state must nest inside the synthetic `__root` wrapper route
 *   (the root navigator's only child). Naming `(app)` at the top level
 *   references unknown routes and react-navigation SILENTLY discards the
 *   reset — no error, no effect.
 * - The wrapper's existing route key must be carried over. Without it the
 *   rehydrate assigns a fresh key and remounts the whole root layout — every
 *   provider (Auth/Theme/SignalR/IAP) plus the switch overlay, which dies
 *   mid-switch and hard-cuts to an unthemed white window. With the key
 *   preserved only the NESTED state resets: the keyless (app) entry below is
 *   rebuilt fresh while the root layout stays mounted.
 */
export function resetAppNavigationTree(navigationContainerRef: NavigationContainerRef) {
  if (!navigationContainerRef.isReady()) {
    return;
  }
  const rootState = navigationContainerRef.getRootState();
  const rootRoute = rootState?.routes?.[rootState?.index ?? 0];
  const appRouteKeyBefore = findAppRouteKey(rootState);

  navigationContainerRef.resetRoot({
    index: 0,
    routes: [
      {
        name: rootRoute?.name ?? '__root',
        ...(rootRoute?.key ? { key: rootRoute.key } : {}),
        state: { index: 0, routes: [{ name: '(app)' }] },
      },
    ],
  });

  // Every tab is cold in the fresh tree; without this clear, revisiting a
  // community would read its long-unmounted tabs as warm (see tabMountRegistry).
  resetMountedTabs();

  if (__DEV__) {
    // Guardrail for the silent-discard failure mode: if an expo-router
    // upgrade changes the private state shape, the reset above no-ops with no
    // error and the cross-community stale-screen bug quietly returns. A fresh
    // (app) route must have a NEW key; read it after the state settles — a
    // synchronous getRootState still returns the pre-rerender state.
    setTimeout(() => {
      const appRouteKeyAfter = findAppRouteKey(navigationContainerRef.getRootState());
      if (appRouteKeyBefore && appRouteKeyAfter === appRouteKeyBefore) {
        console.warn(
          '[resetAppNavigationTree] resetRoot appears to have been silently discarded — ' +
            'the (app) route key did not change. expo-router internals may have changed; ' +
            'community switching will leak stale screens across communities.'
        );
      }
    }, 250);
  }
}
